<script>
import { ErrorMessage, Field, Form as VForm } from "vee-validate";
import { RouterLink } from "vue-router";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { isValidationFailedResponse } from "@/common/api/api.service";
import ContactService from "@/common/api/contact.service";
import { translateFromLaravel } from "@/common/vee-validate-helpers";
import CheckBox from "@/components/CheckBox.vue";
import IconGreenCheckmark from "@/components/icons/green/IconGreenCheckmark.vue";
import IconGreenLetter from "@/components/icons/green/IconGreenLetter.vue";
import IconCross from "@/components/icons/white/IconWhiteCross.vue";
import IconPhone from "@/components/icons/white/IconWhitePhone.vue";

export default {
  name: "RequestAccess",
  components: {
    CheckBox,
    ErrorMessage,
    IconCross,
    IconGreenCheckmark,
    IconGreenLetter,
    IconPhone,
    TgButton,
    Field,
    VForm,
    RouterLink
  },
  props: {
    headline: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showCallRequestForm: false,
      showThankYou: false,
      isRequesting: false
    };
  },
  computed: {
    mailto() {
      return `mailto:${this.$t("requestAccess.data.email")}?Subject=${this.$t(
        "requestAccess.data.emailSubject"
      )}`;
    }
  },
  methods: {
    async onSubmit(values) {
      this.isRequesting = true;

      try {
        await ContactService.callRequest(values.name, values.phoneNumber);
        this.showThankYou = true;
      } catch (error) {
        this.isRequesting = false;
        if (isValidationFailedResponse(error.response)) {
          this.$refs.callRequestForm.setErrors(translateFromLaravel(error.response.data.data));
        }
      }
    }
  }
};
</script>

<template>
  <div class="justify-content-center request-access">
    <div class="request-access__header">
      <RouterLink
        :to="{ name: 'home' }"
        :aria-label="$t('requestAccess.labels.closeButton')"
        :title="$t('requestAccess.labels.closeButton')"
      >
        <IconCross class="request-access__close-icon float-right" role="presentation" />
      </RouterLink>
    </div>

    <template v-if="showThankYou">
      <div class="request-access__content text-center">
        <IconGreenCheckmark class="request-access__thank-you-icon" aria-hidden="true" />
        <h3 class="request-access__headline mb-5">
          {{ $t("requestAccess.headlines.thankYou") }}
        </h3>
      </div>
    </template>
    <template v-else>
      <div class="request-access__content">
        <h3 class="request-access__headline">{{ headline }}</h3>
        <p class="request-access__text pt-1">{{ description }}</p>

        <p class="request-access__text request-access__text--prominent pt-2">
          {{ $t("requestAccess.texts.request") }}
        </p>

        <div class="mb-3">
          <TgButton
            tag="a"
            :href="`tel:${$t('requestAccess.data.phoneNumber')}`"
            :aria-label="$t('requestAccess.labels.callButton')"
          >
            <IconPhone class="request-access__button-icon" role="presentation" />
            {{ $t("requestAccess.data.phoneNumber") }}
          </TgButton>
        </div>
        <div>
          <TgButton
            tag="a"
            variant="outline"
            :href="mailto"
            :aria-label="$t('requestAccess.labels.emailButton')"
          >
            <IconGreenLetter class="request-access__button-icon" />
            {{ $t("requestAccess.data.email") }}
          </TgButton>
        </div>
      </div>

      <div class="request-access__separator"></div>

      <div class="request-access__content">
        <CheckBox v-model:value="showCallRequestForm">
          {{ $t("requestAccess.texts.label") }}
        </CheckBox>

        <div v-if="showCallRequestForm">
          <h3 class="request-access__headline mt-4 mb-3">
            {{ $t("requestAccess.headlines.callRequest") }}
          </h3>

          <VForm v-slot="{ errors, handleSubmit }" ref="callRequestForm" as="">
            <form @submit.prevent="handleSubmit($event, onSubmit)">
              <div class="request-access__input-container">
                <Field
                  name="name"
                  rules="required|max:100"
                  :placeholder="$t('requestAccess.inputs.name')"
                  autocomplete="name"
                  class="request-access__input w-100"
                  :class="{ 'request-access__input--error': errors?.name?.length > 0 }"
                />
                <ErrorMessage name="name" class="request-access__error-message" />
              </div>

              <div class="request-access__input-container request-access__input-container--last">
                <Field
                  name="phoneNumber"
                  rules="required|max:12"
                  :placeholder="$t('requestAccess.inputs.phoneNumber')"
                  type="tel"
                  autocomplete="tel"
                  class="request-access__input w-100"
                  :class="{ 'request-access__input--error': errors?.phoneNumber?.length > 0 }"
                />
                <ErrorMessage name="phoneNumber" class="request-access__error-message" />
              </div>

              <TgButton type="submit" :disabled="isRequesting" full-width>
                {{ $t("requestAccess.texts.button") }}
              </TgButton>
            </form>
          </VForm>

          <i18n-t
            keypath="requestAccess.texts.legal.text"
            tag="p"
            class="request-access__footnote mt-3"
          >
            <template #privacy>
              <RouterLink
                :to="{ name: 'policies', params: { policy: 'privacy' } }"
                target="_blank"
                >{{ $t("requestAccess.texts.legal.privacy") }}</RouterLink
              >
            </template>
            <template #cookie>
              <RouterLink
                :to="{ name: 'policies', params: { policy: 'cookie' } }"
                target="_blank"
                >{{ $t("requestAccess.texts.legal.cookie") }}</RouterLink
              >
            </template>
          </i18n-t>
        </div>
      </div>
    </template>
  </div>
</template>
